import { z } from 'zod';

/**
 * WHAT AN OPERATOR MAY DO TO A PLAYER'S CREDENTIAL — from the console, on the
 * telephone, while the account is being taken from its owner.
 *
 * Its own file rather than more of `player-auth.ts`, and the split is the same
 * one `lists.ts` draws between what a person does to their OWN account and what
 * an operator does to SOMEBODY ELSE'S. Everything in `player-auth.ts` is sent
 * by the account holder and carries no actor but themselves; every shape here
 * is sent by a member of staff about a stranger's account, so every one of them
 * carries a REASON CODE, because "who ended that player's session, and why" is
 * the first question a chargeback, a complaint or a regulator asks and the
 * audit row is the only place it can be answered from.
 *
 * ── WHY THE REASON CODES ARE NOT THE ADMIN ONES ────────────────────────────
 *
 * `adminSignOutReason` in `lists.ts` reads DEVICE_LOST · SUSPECTED_COMPROMISE ·
 * SHARED_CREDENTIAL · ROLE_CHANGE_HYGIENE · AT_THEIR_REQUEST, and four of the
 * five are wrong here. An admin is a colleague with a company laptop and a role
 * that can change; a player is a member of the public whose account holds
 * withdrawable money and whose commonest incident is that somebody else is
 * inside it right now. ROLE_CHANGE_HYGIENE describes an act that cannot happen
 * to a player at all, and the code an operator most needs — the takeover in
 * progress — is not in that list under a name anybody would pick it by.
 *
 * Reusing the admin enum would have cost nothing to write and would have made
 * the one query that matters — "how many account takeovers did we contain last
 * month" — unanswerable, because the two populations would be counted in one
 * bucket under a word chosen for the other.
 *
 * ── WHY THE NOTE IS MANDATORY ON TWO OF THESE AND OPTIONAL ON THE THIRD ────
 *
 * Ending a session and killing a password are CONTAINMENT acts: they are rare,
 * they are consequential, and the next operator to open the account has to be
 * able to read what happened without telephoning the last one. Ten characters
 * is the same floor every other operator write in this workspace holds.
 *
 * Re-sending a confirmation email is not that. It is the single commonest thing
 * a support agent does, it changes nothing about the account, and a mandatory
 * sentence on it buys a trail full of the word "resending" — which is the
 * failure `players.note` in @dayon/rbac argues against in the other direction:
 * make the evidence the hardest thing on the screen to add and the evidence
 * stops being added. The reason CODE is still required, because it is one click
 * and it is what tells a deliverability investigation apart from a player who
 * typed their address wrongly.
 */

/* ══════════════════════════════════════════════════════════════════════════
   ENDING EVERY SESSION A PLAYER HOLDS
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Why the sessions are ending.
 *
 * SUSPECTED_TAKEOVER is first because it is what this control exists for: until
 * it shipped, an operator on an account-takeover call could suspend the account
 * and could not put the attacker out of the session they were already in.
 *
 * AT_PLAYER_REQUEST is the ordinary one — "I was signed in at an internet café
 * in Cebu and I forgot to sign out" — and it is deliberately a different code
 * from the takeover, because they mean opposite things about whether anything
 * else needs to happen to the account afterwards.
 */
export const playerSignOutReason = z.enum([
  'SUSPECTED_TAKEOVER',
  'DEVICE_LOST',
  'SHARED_DEVICE',
  'CREDENTIAL_SHARED',
  'AT_PLAYER_REQUEST',
  'INVESTIGATION',
]);
export type PlayerSignOutReason = z.infer<typeof playerSignOutReason>;

export const signOutPlayerRequest = z.object({
  reasonCode: playerSignOutReason,
  note: z
    .string()
    .min(10, 'Say why the sessions are ending — at least 10 characters.')
    .max(500),
});
export type SignOutPlayerRequest = z.infer<typeof signOutPlayerRequest>;

/* ══════════════════════════════════════════════════════════════════════════
   FORCING A PASSWORD RESET
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Why the password is being taken away.
 *
 * CREDENTIAL_IN_BREACH is the code with no equivalent anywhere else on this
 * platform and it earns its place: the address turning up in a public breach
 * corpus is the commonest reason to invalidate a password that nothing has yet
 * gone wrong with, and it is not a takeover, not a lost device and not a
 * request from the player. Counting it as one of those would hide a
 * platform-wide credential-stuffing exposure inside a support statistic.
 */
export const playerForcedResetReason = z.enum([
  'SUSPECTED_TAKEOVER',
  'CREDENTIAL_IN_BREACH',
  'CREDENTIAL_SHARED',
  'AT_PLAYER_REQUEST',
  'INVESTIGATION',
]);
export type PlayerForcedResetReason = z.infer<typeof playerForcedResetReason>;

export const forcePlayerPasswordResetRequest = z.object({
  reasonCode: playerForcedResetReason,
  note: z
    .string()
    .min(10, 'Say what prompted the reset — at least 10 characters.')
    .max(500),
});
export type ForcePlayerPasswordResetRequest = z.infer<typeof forcePlayerPasswordResetRequest>;

/* ══════════════════════════════════════════════════════════════════════════
   RE-ISSUING A CONFIRMATION LINK
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Why a fresh confirmation link is being sent.
 *
 * Every code here is a DELIVERY fact rather than an account fact, and that is
 * the point of collecting them: NOT_RECEIVED repeated across many players on
 * one day is a deliverability incident and belongs in front of whoever owns the
 * sending domain, while ADDRESS_CORRECTED repeated by one agent is a training
 * question. Neither is visible if the trail only records that a link went out.
 */
export const playerVerificationResendReason = z.enum([
  'NOT_RECEIVED',
  'LINK_EXPIRED',
  'FILTERED_AS_SPAM',
  'ADDRESS_CORRECTED',
  'AT_PLAYER_REQUEST',
]);
export type PlayerVerificationResendReason = z.infer<typeof playerVerificationResendReason>;

export const resendPlayerVerificationRequest = z.object({
  reasonCode: playerVerificationResendReason,
  /** Optional — see the file header for why this one alone has no floor. */
  note: z.string().max(500).optional(),
});
export type ResendPlayerVerificationRequest = z.infer<typeof resendPlayerVerificationRequest>;

/**
 * How many session rows the sessions endpoint returns.
 *
 * Stated here rather than as a literal in the route because the SCREEN has to
 * say the same number when it prints "the 50 most recent" — a list that
 * silently truncates is a list an operator reads as complete, and during a
 * takeover investigation "these are all the sessions" is a claim somebody will
 * act on. The same shape and the same ceiling the admin sessions endpoint uses.
 */
export const PLAYER_SESSION_HISTORY_LIMIT = 50;
